import React, { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function TaskReminders({ api, user }) {
  const [reminders, setReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);

  useEffect(() => {
    // Verificar lembretes a cada minuto
    const interval = setInterval(() => {
      checkReminders();
    }, 60000); // 1 minuto

    // Verificar imediatamente quando carrega
    checkReminders();

    return () => clearInterval(interval);
  }, []);

  const checkReminders = async () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDate = now.toISOString().split('T')[0];

    // Só mostrar lembretes entre 8:00 e 22:00
    if (currentHour < 8 || currentHour > 22) {
      setShowReminders(false);
      return;
    }

    try {
      // Buscar tarefas e atividades do usuário para hoje
      const [tasksResponse, activitiesResponse] = await Promise.all([
        api.get('/tasks/my-tasks'),
        api.get('/activities/my-activities')
      ]);

      const tasks = Array.isArray(tasksResponse.data) ? tasksResponse.data : (tasksResponse.data?.items || []);
      const activities = Array.isArray(activitiesResponse.data) ? activitiesResponse.data : (activitiesResponse.data?.items || []);

      // Filtrar tarefas e atividades para hoje que não estão concluídas
      const todayReminders = [];

      // Processar tarefas
      tasks.forEach(task => {
        const taskDate = new Date(task.due_date || task.created_at).toISOString().split('T')[0];
        if (taskDate === currentDate && !task.completed) {
          todayReminders.push({
            id: task.id,
            type: 'task',
            title: task.title || task.subject,
            description: task.description,
            dueTime: task.due_time || '09:00',
            priority: task.priority || 'medium'
          });
        }
      });

      // Processar atividades
      activities.forEach(activity => {
        const activityDate = new Date(activity.due_date || activity.created_at).toISOString().split('T')[0];
        if (activityDate === currentDate && !activity.completed) {
          todayReminders.push({
            id: activity.id,
            type: 'activity', 
            title: activity.subject || activity.activity_type,
            description: activity.description,
            dueTime: activity.due_time || '09:00',
            priority: activity.priority || 'medium'
          });
        }
      });

      setReminders(todayReminders);
      setShowReminders(todayReminders.length > 0);

    } catch (error) {
      console.error('Erro ao buscar lembretes:', error);
    }
  };

  const markAsCompleted = async (reminder) => {
    try {
      const endpoint = reminder.type === 'task' ? `/tasks/${reminder.id}/complete` : `/activities/${reminder.id}/complete`;
      await api.put(endpoint, { completed: true });
      
      // Remover da lista de lembretes
      setReminders(prev => prev.filter(r => r.id !== reminder.id));
      toast.success(`${reminder.type === 'task' ? 'Tarefa' : 'Atividade'} marcada como concluída!`);
      
      // Se não houver mais lembretes, esconder o componente
      if (reminders.length === 1) {
        setShowReminders(false);
      }
    } catch (error) {
      toast.error('Erro ao marcar como concluída');
      console.error(error);
    }
  };

  const dismissReminder = (id) => {
    setReminders(prev => prev.filter(r => r.id !== id));
    if (reminders.length === 1) {
      setShowReminders(false);
    }
  };

  const dismissAllReminders = () => {
    setShowReminders(false);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-blue-500';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return 'Normal';
    }
  };

  if (!showReminders || reminders.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md space-y-2">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              🔔 Lembretes do Dia
            </h3>
            <div className="flex gap-2">
              <Badge variant="secondary">
                {reminders.length} pendente{reminders.length !== 1 ? 's' : ''}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissAllReminders}
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>
          </div>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {reminders.map((reminder) => (
              <div
                key={reminder.id}
                className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {reminder.type === 'task' ? '📋 Tarefa' : '📅 Atividade'}
                      </span>
                      <Badge 
                        className={`text-xs ${getPriorityColor(reminder.priority)} text-white`}
                      >
                        {getPriorityText(reminder.priority)}
                      </Badge>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {reminder.title}
                    </h4>
                    {reminder.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {reminder.description.substring(0, 80)}...
                      </p>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      🕐 {reminder.dueTime}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => markAsCompleted(reminder)}
                    className="h-7 text-xs"
                  >
                    ✅ Concluir
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissReminder(reminder.id)}
                    className="h-7 text-xs"
                  >
                    💤 Dispensar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}